import { Button } from 'components/ui'
import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Flex, Text } from '@chakra-ui/core'
import UsersList from 'components/CommonComponents/UsersList'
import { SelectionMode } from 'components/CommonComponents/OptimizedGroupsTree/hooks/useSelection'
import useLocaleText from 'components/useLocaleText'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { BaseModalV2 } from 'components/ui/BaseModal'

interface AuditorsModalProps {
  isOpen: boolean
  onClose: () => void
  initialAuditors: string[]
  lockedAuditorIds?: string[]
  onSubmit: (selectedAuditors: string[]) => void
}

export const AuditorsModal: React.FC<AuditorsModalProps> = ({
  isOpen,
  onClose,
  initialAuditors,
  lockedAuditorIds = [],
  onSubmit,
}) => {
  const initialAuditorsWithLocked = useMemo(
    () => Array.from(new Set([...initialAuditors, ...lockedAuditorIds])),
    [initialAuditors, lockedAuditorIds]
  )

  const [selectedAuditors, setSelectedAuditors] = useState<string[]>(initialAuditorsWithLocked)

  const theme = useSelector((state: RootState) => state.general.theme)

  const t_auditors = useLocaleText('auditors') || 'Auditors'
  const t_submit = useLocaleText('save') || 'Save'
  const t_cancel = useLocaleText('cancel') || 'Cancel'

  const handleSelectionChange = useCallback(
    (selectedIds: string[]) => {
      setSelectedAuditors(Array.from(new Set([...selectedIds, ...lockedAuditorIds])))
    },
    [lockedAuditorIds]
  )

  const handleSubmit = useCallback(() => {
    onSubmit(Array.from(new Set([...selectedAuditors, ...lockedAuditorIds])))
    onClose()
  }, [lockedAuditorIds, selectedAuditors, onSubmit, onClose])

  const handleClose = useCallback(() => {
    setSelectedAuditors(initialAuditorsWithLocked)
    onClose()
  }, [initialAuditorsWithLocked, onClose])

  useEffect(() => {
    if (isOpen) {
      setSelectedAuditors(initialAuditorsWithLocked)
    }
  }, [initialAuditorsWithLocked, isOpen])

  return (
    <BaseModalV2
      isOpen={isOpen}
      onClose={handleClose}
      modalProps={{
        closeOnOverlayClick: false,
      }}
      modalOverlayProps={{
        zIndex: 10001,
      }}
      modalContentProps={{
        height: 'auto',
        maxW: '600px',
        zIndex: 10002,
      }}
      modalBodyProps={{ height: 'auto' }}
      header={
        <Text fontSize="18px" fontWeight="600">
          {t_auditors}
        </Text>
      }
      body={
        <UsersList
          showSearch={true}
          selectionMode={SelectionMode.MULTIPLE}
          initialSelectedIds={selectedAuditors}
          lockedSelectedIds={lockedAuditorIds}
          listHeight={400}
          onSelectionChange={handleSelectionChange}
          isShowTags={false}
        />
      }
      footer={
        <Flex w="100%" justifyContent="flex-end" style={{ gap: '12px' }}>
          <Button type="button" variant="ghost" onClick={handleClose}>
            {t_cancel}
          </Button>
          <Button type="button" bg={theme?.elementsColor} color="white" onClick={handleSubmit}>
            {t_submit}
          </Button>
        </Flex>
      }
    />
  )
}
